import React, { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ReuseAvailability } from '@1po/1po-bff-fe-spec/generated/catalog/references/dh/model/ReuseAvailability';
import { ExclamationTriangleIcon, TruckIcon } from 'assets/icons';
import { theme } from 'styles';
import { BlackButton, Flex, Icon, MarginBox, Text, WhiteButton } from 'UI';
import { textFormatter, useBreakpointSelectorFull } from 'utils';
import { getShippingDelayMaxHours } from './reuseDelivery';
import { ReuseImageGallery } from './ReuseImageGallery';
import { SFromLabel, SReusePartCard, SVehicleInfoGrid } from './ReusePartCard.styled';
import { computeInclVat, computeReusePcl, parseReuseNetPrice } from './reusePricing';

interface ReusePartCardBaseProps {
  part: ReuseAvailability;
  vatRate: number;
  // Opens the in-modal product page for this part (wired by ReuseAvailabilitiesModal).
  onSelect?: () => void;
}

export interface ReusePartCardGarageProps extends ReusePartCardBaseProps {
  view: 'GARAGE';
}

export interface ReusePartCardClientProps extends ReusePartCardBaseProps {
  view: 'CLIENT';
  newPcl: number | undefined;
  newGaragePrice: number | undefined;
}

export type ReusePartCardProps = ReusePartCardGarageProps | ReusePartCardClientProps;

// Opisto reuse-availabilities feature is gated to France only — currency is always EUR.
const REUSE_CURRENCY = 'EUR';

const isPresent = (value: string | number | undefined | null): boolean =>
  value !== undefined && value !== null && value !== '';

const ReusePartCard = (props: ReusePartCardProps) => {
  const { part, vatRate } = props;
  const { t } = useTranslation();

  // Keep the single-row layout only on very wide screens; stack into two rows from below xl.
  const isWideLayout = useBreakpointSelectorFull()({
    xs: false,
    sm: false,
    md: false,
    lg: false,
    xl: true,
    xml: true,
    xxl: true,
  });

  const images = part.images ?? [];

  const netPrice = parseReuseNetPrice(part.netPrice);
  const deliveryFee = isPresent(part.deliveryFee) ? Number(part.deliveryFee) || 0 : undefined;

  const garagePriceVatIncluded = useMemo(() => computeInclVat(netPrice, vatRate), [netPrice, vatRate]);

  const clientPrices = props.view === 'CLIENT' ? props : undefined;
  const clientPclVatExcluded = useMemo(
    () =>
      clientPrices && netPrice !== undefined
        ? computeReusePcl(netPrice, clientPrices.newPcl, clientPrices.newGaragePrice)
        : undefined,
    [clientPrices, netPrice],
  );
  const clientPclVatIncluded = useMemo(() => computeInclVat(clientPclVatExcluded, vatRate), [
    clientPclVatExcluded,
    vatRate,
  ]);

  const vehicleInfoItems: { key: string; label: string; value: string | number | undefined | null }[] = [
    { key: 'mileage', label: t('catalog.reuse_availabilities.card.mileage', 'Mileage'), value: part.mileage },
    { key: 'color', label: t('catalog.reuse_availabilities.card.color', 'Color'), value: part.color },
    {
      key: 'color_code',
      label: t('catalog.reuse_availabilities.card.color_code', 'Color code'),
      value: part.colorCode,
    },
    {
      key: 'number_of_doors',
      label: t('catalog.reuse_availabilities.card.number_of_doors', 'Number of doors'),
      value: part.numberOfDoors,
    },
    {
      key: 'condition',
      label: t('catalog.reuse_availabilities.card.condition', 'Condition'),
      value: part.condition,
    },
    {
      key: 'warranty',
      label: t('catalog.reuse_availabilities.card.warranty', 'Warranty'),
      value: isPresent(part.warrantyMonth)
        ? t('catalog.reuse_availabilities.card.warrantyMonths', '{{months}} months', {
            months: part.warrantyMonth,
          })
        : undefined,
    },
  ].filter((item) => isPresent(item.value));

  const renderPrice = (exclVat: number | undefined, inclVat: number | undefined, showPclLabel: boolean) => (
    <Flex direction={'column'}>
      {showPclLabel && <Text type={'light_12_black_65'}>{t('common.price.pcl', 'PCL')}</Text>}
      {exclVat === undefined || inclVat === undefined ? (
        <Text type={'h5_bold'} data-testid={'reuse-part-card-price-unavailable'}>
          {t('common.price.unavailable', 'Price unavailable')}
        </Text>
      ) : (
        <>
          <Text type={'h5_bold'} data-testid={'reuse-part-card-price-excl-vat'}>
            {t('common.price.vat_exclude_price', '{{vat_exclude_price}} VAT. Excl', {
              vat_exclude_price: textFormatter.formatCurrency(exclVat, REUSE_CURRENCY),
            })}
          </Text>
          <Text type={'light_12_black_65'} data-testid={'reuse-part-card-price-incl-vat'}>
            {t('common.price.or_vat_include_price', 'or {{vat_include_price}} VAT. Incl', {
              vat_include_price: textFormatter.formatCurrency(inclVat, REUSE_CURRENCY),
            })}
          </Text>
        </>
      )}
    </Flex>
  );

  const renderLabel = () => {
    const labelParts = [
      part.makeLabel,
      part.modelLabel,
      part.versionLabel,
      part.engineCode,
      part.gearboxCode,
    ].filter((v): v is string => isPresent(v));
    const labelText = (
      <Text type={'lg'} bold>
        {t('catalog.reuse_availabilities.card.from_label', 'From')}:
        {labelParts.length > 0 ? ` ${labelParts.join(', ')}` : ''}
      </Text>
    );
    return props.onSelect ? (
      <SFromLabel type={'button'} data-testid={'reuse-part-card-from-label'} onClick={props.onSelect}>
        {labelText}
      </SFromLabel>
    ) : (
      <Flex direction={'row'} gap={4} data-testid={'reuse-part-card-from-label'} size={0}>
        {labelText}
      </Flex>
    );
  };

  return (
    <SReusePartCard data-testid={'reuse-part-card'} direction={isWideLayout ? 'row' : 'column'}>
      <Flex direction={'row'} gap={16}>
        <Flex size={0}>
          <ReuseImageGallery
            images={images}
            width={200}
            height={200}
            imageTestId={'reuse-part-card-image'}
            mainImageTestId={'reuse-part-card-main-image'}
            placeholderTestId={'reuse-part-card-image-placeholder'}
          />
        </Flex>

        <Flex direction={'column'} gap={10}>
          {renderLabel()}
          <SVehicleInfoGrid>
            {vehicleInfoItems.map((item) => (
              <MarginBox key={item.key} mb={2}>
                <Text type={'base'} bold color="brand_black_light">{`${item.label}: `}</Text>
                <Text
                  type={'base'}
                  color={'grey00_light'}
                  data-testid={`reuse-part-card-${item.key}`}
                >{`${item.value}`}</Text>
              </MarginBox>
            ))}
          </SVehicleInfoGrid>
          {isPresent(part.shippingDelayMax) ? (
            <Flex align={'center'} gap={6} size={'0'}>
              <Icon IconComponent={TruckIcon} size={22} color={theme.color.selected} />
              <Text type={'md'} data-testid={'reuse-part-card-delivery-time'}>
                {t('catalog.reuse_availabilities.card.delivery_time', 'Delivered under {{hours}} hours', {
                  hours: getShippingDelayMaxHours(part.shippingDelayMax),
                })}
              </Text>
            </Flex>
          ) : (
            <Flex align={'center'} gap={6} size={'0'}>
              <Icon IconComponent={TruckIcon} size={22} color={theme.color.selected} />
              <Icon IconComponent={ExclamationTriangleIcon} size={16} color={theme.color.grey55} />
              <Text type={'md'} color={'grey55'} data-testid={'reuse-part-card-delivery-unavailable'}>
                <Trans i18nKey={'catalog.reuse_availabilities.card.delivery_unavailable'}>
                  {'Delivery date: Unavailable'}
                </Trans>
              </Text>
            </Flex>
          )}
        </Flex>
      </Flex>

      <Flex
        direction={isWideLayout ? 'column' : 'row'}
        justify={isWideLayout ? 'flex-start' : 'space-between'}
        align={isWideLayout ? 'stretch' : 'flex-end'}
        gap={16}
        size={0}
      >
        <Flex direction={'column'} gap={4} size={'1'}>
          {props.view === 'GARAGE'
            ? renderPrice(netPrice, garagePriceVatIncluded, false)
            : renderPrice(clientPclVatExcluded, clientPclVatIncluded, true)}
          {deliveryFee !== undefined && (
            <Text type={'light_12_black_65'} data-testid={'reuse-part-card-delivery-fee'}>
              {t('catalog.reuse_availabilities.card.delivery_fee', 'Delivery fees: {{fee}}', {
                fee: textFormatter.formatCurrency(deliveryFee, REUSE_CURRENCY),
              })}
            </Text>
          )}
        </Flex>
        <Flex direction={'column'} gap={10} size={'0'} minWidth={220} maxWidth={220}>
          <BlackButton disabled data-cy={'reuse-part-card-add-to-cart'}>
            {t('cart.action.add_to_cart', 'Add to cart')}
          </BlackButton>
          <WhiteButton disabled data-cy={'reuse-part-card-add-to-estimate'}>
            {t('catalog.action.add_to_estimate', 'Add to estimate')}
          </WhiteButton>
        </Flex>
      </Flex>
    </SReusePartCard>
  );
};

export default ReusePartCard;
